// src/components/onboarding/OnboardingOverlay.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Full-viewport tutorial overlay with spotlight cutout and glassmorphic tooltip.
//
// DESIGN (v4 — 2026-09-08):
//   • The first-login language/settings card is NOT part of this flow. It is
//     WelcomeSetupModal, dismissed on its own terms. This overlay is only ever
//     opened deliberately, and starts at "Create a New Patient".
//   • The overlay has `pointer-events: auto` on the root box, blocking ALL
//     interaction with the underlying app.
//   • For `requiresAction` steps (Create Patient, Send Message) a transparent
//     click-through window is placed over the highlighted element. Clicking it
//     temporarily hides the overlay, clicks the real DOM element underneath,
//     and restores the overlay. The tutorial auto-advances when the real
//     element's click handler fires.
//   • Side-effects on STEP TRANSITIONS:
//       - Leaving "create-patient": programmatically clicks the (+) FAB to
//         expand the patient form.
//       - Entering "patient-form": prefills "Test Patient" in the name input.
//       - Entering "chat-input": prefills a sample clinical note.
//       - Leaving "athena-home": programmatically clicks the logo to navigate
//         back to the dashboard.
//   • For the "create-patient" step the FAB itself also acts as a Next trigger
//     (user can click either the FAB or the Next button).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowForward as NextIcon,
  Close as CloseIcon,
  Check as FinishIcon,
  CheckCircleOutline as DoneIcon,
  TouchApp as ActionIcon,
} from "@mui/icons-material";
import { useTutorial } from "hooks/useOnboardingTutorial";
import { useLegalCompliance } from "hooks/useLegalCompliance";
import { useTranslation } from "../../i18n";
import { athenaColors } from "../../theme/palette";
import {
  Z_OVERLAY,
  Z_CUTOUT,
  Z_CARD,
  onboardingCardSx,
} from "./onboardingChrome";
import api from "../../api/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PADDING = 8;
const GLOW_RADIUS = 4;
const TOOLTIP_W = 360;
const TOOLTIP_H_ESTIMATE = 300;
const GAP = 20;

// Sample clinical note prefilled in the chat input during the tutorial
const SAMPLE_CLINICAL_NOTE =
  "58-year-old male. Patient presents with an asymmetric pigmented lesion on the left " +
  "scapular region, 1.2 cm diameter. Dermoscopy reveals irregular network and blue-white veil. " +
  "Excisional biopsy confirms melanoma, Breslow thickness 0.8 mm, Clark level III, " +
  "no ulceration. BRAF V600E mutation detected.";

// ---------------------------------------------------------------------------
// Helper: set value on a React-controlled input using the native setter
// (avoids "Illegal invocation" by selecting the right prototype).
// ---------------------------------------------------------------------------
function setNativeInputValue(
  input: HTMLInputElement | HTMLTextAreaElement,
  value: string
): void {
  const proto =
    input instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (nativeSetter) {
    nativeSetter.call(input, value);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

// ---------------------------------------------------------------------------
// Helper: find the real <input>/<textarea> inside a container
// ---------------------------------------------------------------------------
function findInput(
  container: HTMLElement
): HTMLInputElement | HTMLTextAreaElement | null {
  if (
    container.tagName === "TEXTAREA" ||
    container.tagName === "INPUT"
  ) {
    return container as HTMLInputElement | HTMLTextAreaElement;
  }
  return container.querySelector("textarea, input") as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OnboardingOverlay: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isActive,
    step,
    currentStep,
    totalSteps,
    next,
    skip,
    tutorialPatientName,
    tutorialPatientId,
  } = useTutorial();
  const { hasPendingLegal } = useLegalCompliance();

  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [measuredTooltipH, setMeasuredTooltipH] = useState(TOOLTIP_H_ESTIMATE);
  const rafRef = useRef<number>(0);

  // Track whether the overlay is temporarily hidden (for click-through)
  const isHiddenRef = useRef(false);

  // Track the last step we scrolled for (so we only scroll once per step)
  const lastScrolledStepRef = useRef<string | null>(null);

  // -----------------------------------------------------------------------
  // Position tracking
  // -----------------------------------------------------------------------
  const updatePosition = useCallback(() => {
    if (!step || step.noTarget) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(
      step.targetSelector
    ) as HTMLElement | null;
    if (el) {
      // Scroll the target into view once per step transition so it is
      // visible even on small viewports where the element starts off-screen.
      if (lastScrolledStepRef.current !== step.id) {
        lastScrolledStepRef.current = step.id;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      const r = el.getBoundingClientRect();
      const nextRect: Rect = {
        top: r.top - PADDING,
        left: r.left - PADDING,
        width: r.width + PADDING * 2,
        height: r.height + PADDING * 2,
      };
      // The MutationObserver on document.body fires on every unrelated DOM
      // change (notification badges, polling, etc.). Only re-render when the
      // spotlight rect actually moved — otherwise each mutation churned a
      // full-viewport SVG + animated glow repaint, which is what made the
      // tutorial feel sluggish.
      setTargetRect((prev) =>
        prev &&
        prev.top === nextRect.top &&
        prev.left === nextRect.left &&
        prev.width === nextRect.width &&
        prev.height === nextRect.height
          ? prev
          : nextRect
      );
    } else {
      setTargetRect(null);
    }
  }, [step]);

  // -----------------------------------------------------------------------
  // Action steps: attach click listener on the real element to auto-advance
  // -----------------------------------------------------------------------
  const actionElRef = useRef<HTMLElement | null>(null);
  const actionHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isActive || !step?.requiresAction || step.noTarget) return;

    const timer = setTimeout(() => {
      const el = document.querySelector(
        step.targetSelector
      ) as HTMLElement | null;
      if (!el) return;

      const handleClick = () => {
        setTimeout(() => next(), 350);
      };
      el.addEventListener("click", handleClick, { once: true });
      actionElRef.current = el;
      actionHandlerRef.current = handleClick;
    }, 100);

    return () => {
      clearTimeout(timer);
      if (actionElRef.current && actionHandlerRef.current) {
        actionElRef.current.removeEventListener(
          "click",
          actionHandlerRef.current
        );
      }
      actionElRef.current = null;
      actionHandlerRef.current = null;
    };
  }, [isActive, step, next]);

  // -----------------------------------------------------------------------
  // ON-ENTER side-effects: run when a step becomes active
  //   - "patient-form": prefill "Test Patient" in the name input
  //   - "chat-input": prefill sample clinical note
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isActive || !step) return;

    if (step.id === "patient-form") {
      const text = t("tutorial.patient_name.placeholder", "Test Patient");
      // The spotlight is on the whole form, but we prefill the NAME input
      // specifically via its data-tour attribute.
      let attempts = 0;
      const tryFill = () => {
        const nameField = document.querySelector(
          "[data-tour='patient-name']"
        ) as HTMLElement | null;
        if (nameField) {
          const input = findInput(nameField);
          if (input) {
            setNativeInputValue(input, text);
            return;
          }
        }
        attempts++;
        if (attempts < 15) setTimeout(tryFill, 200);
      };
      // Longer initial delay — the form is still expanding from the FAB click
      const timer = setTimeout(tryFill, 500);
      return () => clearTimeout(timer);
    }

    if (step.id === "chat-input") {
      let attempts = 0;
      const tryFill = () => {
        // Safety net: ensure the floating chat panel is expanded.
        // The panel root has data-tour="floating-chat-panel" and is ~48px when
        // collapsed, much taller when open.
        const panelRoot = document.querySelector("[data-tour='floating-chat-panel']") as HTMLElement | null;
        if (panelRoot && panelRoot.getBoundingClientRect().height < 60) {
          // Panel still collapsed — click expand, then retry after the transition.
          const expandBtn = document.querySelector("[data-tour='floating-chat-expand']") as HTMLElement | null;
          if (expandBtn) expandBtn.click();
          attempts++;
          if (attempts < 15) setTimeout(tryFill, 350);
          return;
        }
        // Panel is open — locate and fill the chat input.
        const container = document.querySelector(
          step.targetSelector
        ) as HTMLElement | null;
        if (container) {
          const input = findInput(container);
          if (input) {
            setNativeInputValue(input, SAMPLE_CLINICAL_NOTE);
            return;
          }
        }
        attempts++;
        if (attempts < 15) setTimeout(tryFill, 200);
      };
      const timer = setTimeout(tryFill, 300);
      return () => clearTimeout(timer);
    }

    // Collapse the floating chat panel when entering the processing step
    // so the user can see the tables once they appear.
    if (step.id === "extraction-processing") {
      const panel = document.querySelector("[data-tour='floating-chat-panel']") as HTMLElement | null;
      if (panel && panel.getBoundingClientRect().height > 60) {
        const expandBtn = document.querySelector("[data-tour='floating-chat-expand']") as HTMLElement | null;
        if (expandBtn) expandBtn.click();
      }
    }

    // Pre-fill the search bar with "Test Patient" on the search step
    if (step.id === "search-patient") {
      const searchText = t("tutorial.patient_name.placeholder", "Test Patient");
      let attempts = 0;
      const tryFill = () => {
        const container = document.querySelector(
          step.targetSelector
        ) as HTMLElement | null;
        if (container) {
          const input = findInput(container);
          if (input) {
            setNativeInputValue(input, searchText);
            return;
          }
        }
        attempts++;
        if (attempts < 15) setTimeout(tryFill, 200);
      };
      const timer = setTimeout(tryFill, 500);
      return () => clearTimeout(timer);
    }
  }, [isActive, step, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Transition delay: after creating a patient the app navigates to
  // /patients/{id}/details. The page needs ~2 s to fully render.
  // Also used after athena-home -> search-patient navigation.
  // During this delay we show only the dark overlay (no spotlight/tooltip).
  // -----------------------------------------------------------------------
  const [enterDelayActive, setEnterDelayActive] = useState(false);

  useEffect(() => {
    if (
      step?.id === "sidebar-overview" ||
      step?.id === "data-panel-overview" ||
      step?.id === "chat-input" ||
      step?.id === "search-patient" ||
      step?.id === "patient-form"
    ) {
      setEnterDelayActive(true);
      const delay = step.id === "chat-input" ? 600
        : step.id === "patient-form" ? 500
        : 2000;
      const timer = setTimeout(() => setEnterDelayActive(false), delay);
      return () => clearTimeout(timer);
    }
    setEnterDelayActive(false);
  }, [step]);

  // -----------------------------------------------------------------------
  // Processing step: rotating messages + seed API call
  // -----------------------------------------------------------------------
  const PROCESSING_MESSAGES = [
    "tutorial.processing.msg_analyzing",
    "tutorial.processing.msg_detecting",
    "tutorial.processing.msg_structuring",
    "tutorial.processing.msg_building",
  ];
  const PROCESSING_FALLBACKS = [
    "Analyzing clinical document...",
    "Detecting cancer type...",
    "Structuring clinical data...",
    "Building clinical tables...",
  ];
  const MSG_INTERVAL = 2000;
  const MIN_PROCESSING_TIME = 8000;

  const [processingMsgIdx, setProcessingMsgIdx] = useState(0);
  const [processingDone, setProcessingDone] = useState(false);
  const processingApiDone = useRef(false);
  const processingTimerDone = useRef(false);

  useEffect(() => {
    if (!isActive || step?.id !== "extraction-processing") {
      setProcessingMsgIdx(0);
      setProcessingDone(false);
      processingApiDone.current = false;
      processingTimerDone.current = false;
      return;
    }

    const msgInterval = setInterval(() => {
      setProcessingMsgIdx((prev) =>
        prev < PROCESSING_MESSAGES.length - 1 ? prev + 1 : prev
      );
    }, MSG_INTERVAL);

    const minTimer = setTimeout(() => {
      processingTimerDone.current = true;
      if (processingApiDone.current) setProcessingDone(true);
    }, MIN_PROCESSING_TIME);

    let safetyTimer: ReturnType<typeof setTimeout> | null = null;
    let tablesReadyHandler: (() => void) | null = null;

    if (tutorialPatientId) {
      tablesReadyHandler = () => {
        if (safetyTimer) clearTimeout(safetyTimer);
        processingApiDone.current = true;
        if (processingTimerDone.current) setProcessingDone(true);
      };
      window.addEventListener("tutorial-tables-ready", tablesReadyHandler, { once: true });

      safetyTimer = setTimeout(() => {
        if (tablesReadyHandler) window.removeEventListener("tutorial-tables-ready", tablesReadyHandler);
        processingApiDone.current = true;
        if (processingTimerDone.current) setProcessingDone(true);
      }, 30000);

      api
        .post(`/tutorial/seed/${tutorialPatientId}/`)
        .then(() => {
          window.dispatchEvent(new Event("tutorial-seed-complete"));
        })
        .catch((err) => {
          console.error("Tutorial seed failed:", err?.response?.status, err?.response?.data);
          if (tablesReadyHandler) window.removeEventListener("tutorial-tables-ready", tablesReadyHandler);
          if (safetyTimer) clearTimeout(safetyTimer);
          processingApiDone.current = true;
          if (processingTimerDone.current) setProcessingDone(true);
        });
    } else {
      processingApiDone.current = true;
    }

    return () => {
      clearInterval(msgInterval);
      clearTimeout(minTimer);
      if (safetyTimer) clearTimeout(safetyTimer);
      if (tablesReadyHandler) window.removeEventListener("tutorial-tables-ready", tablesReadyHandler);
    };
  }, [isActive, step?.id, tutorialPatientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // handleNext — performs EXIT side-effects, then advances
  // -----------------------------------------------------------------------
  const handleNext = useCallback(() => {
    // EXIT side-effect: expand the patient form by clicking the FAB
    if (step?.id === "create-patient") {
      const formAlreadyOpen = !!document.querySelector("[data-tour='patient-name']");
      if (formAlreadyOpen) {
        // Form already visible — skip the FAB click and advance immediately
        next();
        return;
      }
      const fab = document.querySelector(
        "[data-tour='create-patient']"
      ) as HTMLElement | null;
      if (fab) {
        if (overlayRef.current) overlayRef.current.style.display = "none";
        fab.click();
        if (overlayRef.current) overlayRef.current.style.display = "";
      }
      // Don't call next() here — the useEffect FAB listener handles advance
      return;
    }

    // EXIT side-effect: clear the search bar when leaving the search step
    if (step?.id === "search-patient") {
      const container = document.querySelector(
        "[data-tour='patient-search-area']"
      ) as HTMLElement | null;
      if (container) {
        const input = findInput(container);
        if (input) {
          setNativeInputValue(input, "");
        }
      }
    }

    // EXIT side-effect: navigate home by clicking the Athena logo
    if (step?.id === "athena-home") {
      const logo = document.querySelector(
        "[data-tour='athena-logo']"
      ) as HTMLElement | null;
      if (logo) {
        if (overlayRef.current) overlayRef.current.style.display = "none";
        logo.click();
        if (overlayRef.current) overlayRef.current.style.display = "";
      }
      // Fallback: ensure we always navigate to the dashboard
      navigate("/dashboard", { state: { resetView: Date.now() } });
    }

    next();
  }, [step, next]);

  // -----------------------------------------------------------------------
  // Allow the FAB click to also advance (user clicks (+) OR Next on step 1)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isActive || step?.id !== "create-patient") return;

    let cleanup: (() => void) | undefined;

    const tryAttach = () => {
      const fab = document.querySelector(
        "[data-tour='create-patient']"
      ) as HTMLElement | null;
      if (!fab) return;

      const handler = () => {
        setTimeout(() => {
          // Guard: if the click toggled the form CLOSED (was already open),
          // re-click the FAB to re-open it before advancing.
          const formVisible = !!document.querySelector("[data-tour='patient-name']");
          if (!formVisible) {
            if (overlayRef.current) overlayRef.current.style.display = "none";
            fab.click();
            if (overlayRef.current) overlayRef.current.style.display = "";
            setTimeout(() => next(), 350);
          } else {
            next();
          }
        }, 350);
      };
      fab.addEventListener("click", handler, { once: true });
      cleanup = () => fab.removeEventListener("click", handler);
    };

    const timer = setTimeout(tryAttach, 150);
    return () => {
      clearTimeout(timer);
      cleanup?.();
    };
  }, [isActive, step, next]);

  // -----------------------------------------------------------------------
  // Measure actual tooltip height after render
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!tooltipRef.current) return;
    const h = tooltipRef.current.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - measuredTooltipH) > 5) {
      setMeasuredTooltipH(h);
    }
  });

  // Reset measured height and skip confirm when step changes
  useEffect(() => {
    setMeasuredTooltipH(TOOLTIP_H_ESTIMATE);
    setShowSkipConfirm(false);
  }, [currentStep]);

  // -----------------------------------------------------------------------
  // Reposition on scroll / resize / DOM changes
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isActive) return;

    updatePosition();

    const onUpdate = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    window.addEventListener("scroll", onUpdate, true);
    window.addEventListener("resize", onUpdate);
    const observer = new MutationObserver(onUpdate);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener("scroll", onUpdate, true);
      window.removeEventListener("resize", onUpdate);
      observer.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
    // enterDelayActive is intentionally included: when the delay ends we
    // re-measure so the spotlight lands on the now-fully-visible element.
  }, [isActive, updatePosition, enterDelayActive]);

  // -----------------------------------------------------------------------
  // Guard: don't render during legal gate
  // (All hooks MUST be above this early return — React rules-of-hooks.)
  // -----------------------------------------------------------------------
  if (!isActive || !step || hasPendingLegal) return null;

  // During enterDelay (e.g., page transition after patient creation),
  // show only the dark overlay — no spotlight, no tooltip.
  if (enterDelayActive) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: Z_OVERLAY,
          pointerEvents: "auto",
          background: alpha(theme.palette.common.black, 0.55),
        }}
      />
    );
  }

  const isActionStep = !!step.requiresAction;
  const isLastStep = currentStep === totalSteps - 1;
  const isProcessingStep = !!step.isProcessingStep;
  const isCentered = !!step.noTarget;
  const isExploreStep = !!step.allowExplore;

  // Suppress spotlight/tooltip until targetRect is measured (modal-race fix)
  const targetReady = isCentered || targetRect !== null;

  // For "create-patient" step we also allow clicks on the FAB cutout
  const isFabStep = step.id === "create-patient";
  const allowCutoutClicks = isActionStep || isFabStep;

  // Dynamic description for search step
  let description = t(step.description, step.description);
  if (step.id === "search-patient") {
    if (tutorialPatientName) {
      description = t(
        "tutorial.search_patient.description_with_name",
        `We've typed "${tutorialPatientName}" for you. Results appear below as you type.`
      ).replace("{patientName}", tutorialPatientName);
    } else {
      description = t(
        "tutorial.search_patient.description_no_patient",
        "Start typing a name to find any patient instantly."
      );
    }
  }

  // Tooltip positioning — center when no target found
  const tooltipStyle = (isCentered || !targetRect)
    ? ({
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      } as Record<string, string | number>)
    : getTooltipPosition(
        targetRect,
        step.placement,
        window.innerWidth,
        window.innerHeight,
        measuredTooltipH
      );

  // Click-through handler for the cutout window.
  // `elementFromPoint` can return SVG elements (e.g., the send icon) which
  // lack `.click()`. Walk up the DOM to find the nearest clickable element.
  const handleCutoutClick = (e: React.MouseEvent) => {
    if (!overlayRef.current) return;
    // Temporarily hide overlay to expose the real DOM
    overlayRef.current.style.display = "none";
    isHiddenRef.current = true;
    const pointEl = document.elementFromPoint(e.clientX, e.clientY);
    overlayRef.current.style.display = "";
    isHiddenRef.current = false;
    if (pointEl) {
      // Walk up to the nearest element that has a .click() method
      let target: Element | null = pointEl;
      while (target && typeof (target as HTMLElement).click !== "function") {
        target = target.parentElement;
      }
      if (target) (target as HTMLElement).click();
    }
  };

  return (
    <Box
      ref={overlayRef}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: Z_OVERLAY,
        // In explore mode clicks pass through to the app; only the
        // tooltip card (pointerEvents: "auto") captures interaction.
        pointerEvents: isExploreStep ? "none" : "auto",
      }}
    >
      {/* Dark overlay with cutout */}
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        <defs>
          <mask id="tutorial-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && targetReady && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx={GLOW_RADIUS + 4}
                ry={GLOW_RADIUS + 4}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill={isCentered
            ? alpha(theme.palette.common.black, 0.7)
            : alpha(theme.palette.common.black, isExploreStep ? 0.35 : 0.55)}
          mask={isCentered || !targetReady ? undefined : "url(#tutorial-mask)"}
        />
      </svg>

      {/* Click-through window for FAB and action steps */}
      {allowCutoutClicks && targetRect && targetReady && (
        <Box
          sx={{
            position: "absolute",
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            pointerEvents: "auto",
            zIndex: Z_CUTOUT,
            background: "transparent",
            cursor: "pointer",
          }}
          onClick={handleCutoutClick}
        />
      )}

      {/* Glow ring around the target */}
      {targetRect &&
        targetReady &&
        !isCentered &&
        (() => {
          const hlStyle = step.highlightStyle || "default";
          const isOrange =
            hlStyle === "orange" || hlStyle === "blink-orange";
          const ringColor = isOrange
            ? theme.palette.warning.main
            : athenaColors.periwinkle;
          const animName =
            hlStyle === "blink-orange"
              ? "tutorialBlinkOrange"
              : "tutorialPulse";

          return (
            <Box
              sx={{
                position: "absolute",
                top: targetRect.top - 2,
                left: targetRect.left - 2,
                width: targetRect.width + 4,
                height: targetRect.height + 4,
                borderRadius: `${GLOW_RADIUS + 6}px`,
                border: `2px solid ${alpha(ringColor, 0.75)}`,
                boxShadow: `0 0 20px ${alpha(
                  ringColor,
                  0.4
                )}, inset 0 0 20px ${alpha(ringColor, 0.1)}`,
                animation: `${animName} ${
                  hlStyle === "blink-orange" ? "1s" : "2s"
                } ease-in-out infinite`,
                pointerEvents: "none",
                "@keyframes tutorialPulse": {
                  "0%, 100%": {
                    boxShadow: `0 0 20px ${alpha(
                      athenaColors.periwinkle,
                      0.4
                    )}, inset 0 0 20px ${alpha(athenaColors.periwinkle, 0.1)}`,
                  },
                  "50%": {
                    boxShadow: `0 0 35px ${alpha(
                      athenaColors.periwinkle,
                      0.65
                    )}, inset 0 0 30px ${alpha(athenaColors.periwinkle, 0.2)}`,
                  },
                },
                "@keyframes tutorialBlinkOrange": {
                  "0%, 100%": {
                    boxShadow: `0 0 12px ${alpha(ringColor, 0.3)}`,
                    borderColor: alpha(ringColor, 0.5),
                  },
                  "50%": {
                    boxShadow: `0 0 40px ${alpha(
                      ringColor,
                      0.8
                    )}, inset 0 0 20px ${alpha(ringColor, 0.15)}`,
                    borderColor: alpha(ringColor, 0.95),
                  },
                },
              }}
            />
          );
        })()}

      {/* Tooltip card — always visible so the user can navigate */}
      <Box
        ref={tooltipRef}
        sx={{
          position: isCentered ? "fixed" : "absolute",
          ...tooltipStyle,
          ...onboardingCardSx(isCentered),
          width: { xs: 280, sm: TOOLTIP_W },
          zIndex: Z_CARD,
          pointerEvents: "auto",
        }}
      >
        {/* ─── Processing Step Card Body ─── */}
        {isProcessingStep ? (
          <>
            <Box sx={{ textAlign: "center", py: 2 }}>
              {processingDone ? (
                <DoneIcon
                  sx={{
                    fontSize: 48,
                    color: "#4caf50",
                    mb: 2,
                    animation: "tutorialFadeIn 0.3s ease-out",
                  }}
                />
              ) : (
                <CircularProgress
                  size={48}
                  sx={{
                    color: athenaColors.periwinkle,
                    mb: 2,
                  }}
                />
              )}
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 1.5,
                  color: theme.palette.common.white,
                }}
              >
                {processingDone
                  ? t("tutorial.processing.done_title", "Done!")
                  : t(step.title, step.title)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha(theme.palette.common.white, 0.85),
                  lineHeight: 1.6,
                  minHeight: 24,
                  transition: "opacity 0.4s ease",
                }}
              >
                {processingDone
                  ? t("tutorial.processing.done_description", "Clinical data has been extracted successfully.")
                  : t(
                      PROCESSING_MESSAGES[processingMsgIdx],
                      PROCESSING_FALLBACKS[processingMsgIdx]
                    )}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={processingDone
                ? 100
                : Math.min(
                    95,
                    ((processingMsgIdx + 1) / PROCESSING_MESSAGES.length) * 95
                  )}
              sx={{
                mt: 1,
                mb: 2,
                height: 3,
                borderRadius: 2,
                backgroundColor: alpha(athenaColors.periwinkle, 0.15),
                "& .MuiLinearProgress-bar": {
                  backgroundColor: processingDone ? "#4caf50" : athenaColors.periwinkle,
                  transition: "transform 0.6s ease",
                },
              }}
            />
            <Box sx={{ textAlign: "center" }}>
              {processingDone ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<NextIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    backgroundColor: athenaColors.periwinkle,
                    color: theme.palette.common.white,
                    animation: "tutorialFadeIn 0.3s ease-out",
                    "&:hover": {
                      backgroundColor: alpha(athenaColors.periwinkle, 0.85),
                    },
                  }}
                >
                  {t("tutorial.continue", "Continue")}
                </Button>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.4),
                    fontStyle: "italic",
                  }}
                >
                  {t("tutorial.processing.please_wait", "Please wait...")}
                </Typography>
              )}
            </Box>
          </>
        ) : (
        <>
        {/* Step counter */}
        <Typography
          variant="caption"
          sx={{
            color: athenaColors.periwinkle,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 0.5,
            display: "block",
          }}
        >
          {currentStep + 1} {t("tutorial.of", "of")} {totalSteps}
        </Typography>

        {/* Title */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 1, color: theme.palette.common.white }}
        >
          {t(step.title, step.title)}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            mb: 2.5,
            color: alpha(theme.palette.common.white, 0.8),
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>

        {/* Navigation buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {showSkipConfirm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: alpha(theme.palette.common.white, 0.7), lineHeight: 1.4, fontSize: "0.72rem" }}
              >
                {t(
                  "tutorial.skip_confirm_msg",
                  "You can restart this tutorial anytime from Settings in the top navigation bar."
                )}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => { setShowSkipConfirm(false); skip(); }}
                  sx={{
                    textTransform: "none",
                    color: athenaColors.orange,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    minWidth: 0,
                    px: 1,
                  }}
                >
                  {t("tutorial.skip_confirm_yes", "End tutorial")}
                </Button>
                <Button
                  size="small"
                  onClick={() => setShowSkipConfirm(false)}
                  sx={{
                    textTransform: "none",
                    color: alpha(theme.palette.common.white, 0.6),
                    fontSize: "0.75rem",
                    minWidth: 0,
                    px: 1,
                  }}
                >
                  {t("tutorial.skip_confirm_no", "Continue")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              size="small"
              onClick={() => setShowSkipConfirm(true)}
              sx={{
                textTransform: "none",
                color: alpha(theme.palette.common.white, 0.5),
                fontSize: "0.8rem",
                "&:hover": { color: alpha(theme.palette.common.white, 0.8) },
              }}
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            >
              {t("tutorial.skip", "Skip")}
            </Button>
          )}

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {isActionStep ? (
              // Action steps: show hint + Next button
              // User can click the highlighted element OR press Next
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: athenaColors.periwinkle,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    animation:
                      "tutorialHintPulse 2s ease-in-out infinite",
                    "@keyframes tutorialHintPulse": {
                      "0%, 100%": { opacity: 0.85 },
                      "50%": { opacity: 1 },
                    },
                  }}
                >
                  <ActionIcon sx={{ fontSize: 14 }} />
                  {t(
                    step.actionHint || "tutorial.click_to_continue",
                    "Click the highlighted element"
                  )}
                </Typography>
              </Box>
            ) : (
              // Standard Next / Finish button
              <Button
                size="small"
                variant="contained"
                onClick={handleNext}
                endIcon={
                  isLastStep ? (
                    <FinishIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <NextIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  minWidth: 80,
                  fontWeight: 600,
                  backgroundColor: athenaColors.periwinkle,
                  color: theme.palette.common.white,
                  "& .MuiButton-endIcon, & .MuiButton-startIcon": {
                    color: "inherit",
                  },
                  "&:hover": {
                    backgroundColor: alpha(
                      athenaColors.periwinkle,
                      0.85
                    ),
                  },
                }}
              >
                {isLastStep
                  ? t("tutorial.finish", "Finish")
                  : t("tutorial.next", "Next")}
              </Button>
            )}
          </Box>
        </Box>
        </>)}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Tooltip positioning — overlap-aware with smart fallback
// ---------------------------------------------------------------------------

function rectsOverlap(a: Rect, b: Rect): boolean {
  return !(
    a.left >= b.left + b.width ||
    a.left + a.width <= b.left ||
    a.top >= b.top + b.height ||
    a.top + a.height <= b.top
  );
}

function getTooltipPosition(
  rect: Rect | null,
  placement: string,
  viewW: number,
  viewH: number,
  tooltipH: number = TOOLTIP_H_ESTIMATE
): Record<string, number | string> {
  if (!rect) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const clampLeft = (l: number) =>
    Math.max(12, Math.min(l, viewW - TOOLTIP_W - 12));

  const tryPlacement = (p: string): Record<string, number> | null => {
    let tooltipRect: Rect;
    let result: Record<string, number>;

    switch (p) {
      case "bottom": {
        const top = rect.top + rect.height + GAP;
        const left = clampLeft(rect.left);
        if (top + tooltipH > viewH - 12) return null;
        result = { top, left };
        tooltipRect = { top, left, width: TOOLTIP_W, height: tooltipH };
        break;
      }
      case "top": {
        const top = rect.top - GAP - tooltipH;
        const left = clampLeft(rect.left);
        if (top < 12) return null;
        result = { top, left };
        tooltipRect = { top, left, width: TOOLTIP_W, height: tooltipH };
        break;
      }
      case "right": {
        const left = rect.left + rect.width + GAP;
        const top = Math.max(
          12,
          Math.min(rect.top, viewH - tooltipH - 12)
        );
        if (left + TOOLTIP_W > viewW - 12) return null;
        result = { top, left };
        tooltipRect = { top, left, width: TOOLTIP_W, height: tooltipH };
        break;
      }
      case "left": {
        const left = rect.left - TOOLTIP_W - GAP;
        const top = Math.max(
          12,
          Math.min(rect.top, viewH - tooltipH - 12)
        );
        if (left < 12) return null;
        result = { top, left };
        tooltipRect = { top, left, width: TOOLTIP_W, height: tooltipH };
        break;
      }
      default:
        return null;
    }

    if (rectsOverlap(tooltipRect, rect)) return null;
    return result;
  };

  const fallbackOrder: Record<string, string[]> = {
    left: ["left", "right", "bottom", "top"],
    right: ["right", "left", "bottom", "top"],
    top: ["top", "bottom", "right", "left"],
    bottom: ["bottom", "top", "right", "left"],
  };

  const candidates = fallbackOrder[placement] || fallbackOrder.bottom;
  for (const candidate of candidates) {
    const result = tryPlacement(candidate);
    if (result) return result;
  }

  return {
    top: Math.max(12, rect.top - GAP - tooltipH),
    left: clampLeft(rect.left),
  };
}

export default OnboardingOverlay;
